import { MoreLink } from "@/components/list/more-link";
import { RefreshButton } from "@/components/list/refresh-button";
import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { swrKey } from "@/lib/swr";
import { VersionList } from "@/screens/Versions/version-card";
import { ListLoading } from "@/components/list/status-text";
import type { Version } from "@/types/domain";

const DEFAULT_SHOWN = 10;

const AppVersions = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [expanded, setExpanded] = useState(false);
  // By default show all historical versions of this app (consistent with the overview page's History versions); URL ?state= can filter by state.
  const stateParam = searchParams.get("state");
  const states = stateParam
    ? stateParam.split(",").map((s) => parseInt(s, 10))
    : undefined;

  const params: Record<string, unknown> = { pid: id, pageIndex: 0, pageSize: 500 };
  if (states) params.state = states;

  const { data, isLoading, isValidating, mutate } = useSWR<{
    data: Version[];
    total: number;
  }>(swrKey("/versions", params), { keepPreviousData: true });

  // Sort by versionCode numerically descending — the app's own monotonic
  // timeline. Sorting by onlineAt looked right but NULL onlineAt doesn't mean
  // "not yet released": rejected/abandoned versions never get one and floated
  // above the current pipeline (and NULL-vs-NULL compared as NaN). versionCode
  // is a string column with occasional dirty suffixes, hence parseInt.
  const versions: Version[] = [...(data?.data ?? [])].sort((a: Version, b: Version) => {
    const ca = parseInt(a.versionCode ?? "", 10) || 0;
    const cb = parseInt(b.versionCode ?? "", 10) || 0;
    if (cb !== ca) return cb - ca;
    const ta = a.onlineAt ? new Date(a.onlineAt).getTime() : 0;
    const tb = b.onlineAt ? new Date(b.onlineAt).getTime() : 0;
    return tb - ta;
  });

  // Show the first 10 by default; "More" expands the rest in place.
  const shown = expanded ? versions : versions.slice(0, DEFAULT_SHOWN);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex w-full justify-between">
        {isLoading && !data && <ListLoading>Loading versions…</ListLoading>}
        <div className="flex space-x-2 ml-auto">
          <RefreshButton spinning={isValidating} onClick={() => mutate()} />
        </div>
      </div>
      <VersionList versions={shown} isLoading={isLoading && !data} groupByState={false} />
      {!expanded && versions.length > DEFAULT_SHOWN && (
        <MoreLink total={versions.length} onClick={() => setExpanded(true)} />
      )}
    </div>
  );
};

export default AppVersions;
